"use client";
import { downloadCertificate, getDayOfWeek, formatTime } from "@/components/ui/games/GameCertificate";
import { OtherGames } from "@/components/ui/games/OtherGames";
import JSConfetti from "js-confetti";
import { useCallback, useEffect, useState, Suspense } from "react";
import { useSearchParams } from "next/navigation";

interface Group {
  color: "yellow" | "green" | "blue" | "purple";
  theme: string;
  words: string[];
}

interface GameData {
  day: number;
  groups: Group[];
}

// Allowed colors and type guard to narrow JSON input to our union type
const VALID_COLORS = ["yellow", "green", "blue", "purple"] as const;
const isValidColor = (color: string): color is Group["color"] => (VALID_COLORS as readonly string[]).includes(color);

function ConnectionsPageContent() {
  const searchParams = useSearchParams();
  const isClubhouse = searchParams?.get("id") === "clubhouse";
  const [playerName, setPlayerName] = useState<string>("");
  const [gameStarted, setGameStarted] = useState<boolean>(false);
  const [gameData, setGameData] = useState<GameData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedWords, setSelectedWords] = useState<string[]>([]);
  const [foundGroups, setFoundGroups] = useState<Group[]>([]);
  const [mistakes, setMistakes] = useState<number>(0);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [shake, setShake] = useState<boolean>(false);
  const [gameState, setGameState] = useState<"playing" | "won" | "lost">("playing");
  const [shuffledWords, setShuffledWords] = useState<string[]>([]);
  const [shareMessage, setShareMessage] = useState<string>("");
  const [guessHistory, setGuessHistory] = useState<string[][]>([]);

  const MAX_MISTAKES = 4;
  const GROUP_COLORS: Record<Group["color"], string> = {
    yellow: "bg-yellow-200 text-yellow-800 border-yellow-300",
    green: "bg-green-200 text-green-800 border-green-300",
    blue: "bg-blue-200 text-blue-800 border-blue-300",
    purple: "bg-purple-200 text-purple-800 border-purple-300",
  };

  // Fetch game data on mount
  useEffect(() => {
    const fetchGameData = async () => {
      try {
        const { default: raw } = await import("./connections_words.json");

        // Normalize raw JSON into strongly-typed GameData
        const typedData: GameData[] = (
          raw as Array<{ day: number; groups: Array<{ color: string; theme: string; words: string[] }> }>
        ).map((g) => ({
          day: g.day,
          groups: g.groups.map((grp) => ({
            color: isValidColor(grp.color) ? grp.color : "yellow",
            theme: grp.theme,
            words: grp.words,
          })),
        }));

        // Calculate which day's puzzle to show (1-indexed in JSON) using a static start date (Aug 24, 2024)
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const startDate = new Date(2025, 7, 26); // Months are 0-indexed: 7 = August
        startDate.setHours(0, 0, 0, 0);
        const msPerDay = 1000 * 60 * 60 * 24;
        const daysSinceStart = Math.floor((today.getTime() - startDate.getTime()) / msPerDay);

        // Cycle through all available puzzles
        const totalDays = typedData.length;
        const dayNumber = (((daysSinceStart % totalDays) + totalDays) % totalDays) + 1;

        // Find the game for this day
        const todaysGame = typedData.find((game) => game.day === dayNumber);

        if (todaysGame) {
          setGameData(todaysGame);

          // Shuffle all words from all groups
          const allWords: string[] = [];
          todaysGame.groups.forEach((group: Group) => {
            allWords.push(...group.words);
          });
          setShuffledWords(shuffleArray(allWords));
        }
      } catch (error) {
        console.error("Error fetching game data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchGameData();
  }, []);

  // Live timer
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (startTime && gameState === "playing") {
      interval = setInterval(() => {
        setCurrentTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [startTime, gameState]);

  const shuffleArray = <T,>(array: T[]): T[] => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  const startGame = () => {
    if (playerName.trim().length < 2) {
      setErrorMessage("Please enter your name (at least 2 characters)");
      return;
    }
    setGameStarted(true);
    setStartTime(Date.now());
    setErrorMessage("");
  };

  const toggleWordSelection = (word: string) => {
    if (gameState !== "playing") return;

    if (selectedWords.includes(word)) {
      setSelectedWords(selectedWords.filter((w) => w !== word));
    } else if (selectedWords.length < 4) {
      setSelectedWords([...selectedWords, word]);
    }
  };

  const checkSelection = () => {
    if (selectedWords.length !== 4 || gameState !== "playing" || !gameData) return;

    // Add current selection to guess history
    const newGuessHistory = [...guessHistory, [...selectedWords]];
    setGuessHistory(newGuessHistory);

    // Check if the selected words form a group
    const foundGroup = gameData.groups.find((group) => {
      const groupWords = group.words.map((w) => w.toUpperCase());
      const selected = selectedWords.map((w) => w.toUpperCase());
      return selected.every((word) => groupWords.includes(word));
    });

    if (foundGroup) {
      // Correct guess
      const newFoundGroups = [...foundGroups, foundGroup];
      setFoundGroups(newFoundGroups);

      // Remove found words from the board
      const remainingWords = shuffledWords.filter(
        (word) => !foundGroup.words.map((w) => w.toUpperCase()).includes(word.toUpperCase())
      );
      setShuffledWords(remainingWords);
      setSelectedWords([]);

      // Check if won
      if (newFoundGroups.length === gameData.groups.length) {
        setGameState("won");
        const jsConfetti = new JSConfetti();
        jsConfetti.addConfetti();
      }
    } else {
      // Wrong guess
      const newMistakes = mistakes + 1;
      setMistakes(newMistakes);
      showError("Those words don't form a group!");

      if (newMistakes >= MAX_MISTAKES) {
        setGameState("lost");
      }

      setSelectedWords([]);
    }
  };

  const showError = (message: string) => {
    setErrorMessage(message);
    setShake(true);
    setTimeout(() => {
      setErrorMessage("");
      setShake(false);
    }, 2000);
  };

  const deselectAll = () => {
    setSelectedWords([]);
  };

  const shuffleWords = () => {
    if (gameState !== "playing") return;
    setShuffledWords(shuffleArray(shuffledWords));
  };

  const handleDownloadCertificate = async () => {
    await downloadCertificate(
      {
        gameName: "Nowadays Connections",
        playerName: playerName,
        stats: [
          getDayOfWeek(),
          `${foundGroups.length}/4 groups found`,
          `${mistakes} ${mistakes === 1 ? "mistake" : "mistakes"}`,
          formatTime(currentTime),
        ],
        subtitle: "Connections Master!",
        backgroundColor: { start: "#8b5cf6", end: "#7c3aed" },
      },
      `connections-${playerName}-${getDayOfWeek().toLowerCase()}.png`
    );
  };

  const generateShareText = useCallback(() => {
    if (!gameData) return "";

    // Get today's date in a readable format
    const today = new Date();
    const dateString = today.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

    let shareText = `Connections\n${dateString}\n`;

    const colorEmojis: Record<Group["color"], string> = {
      yellow: "🟨",
      green: "🟩",
      blue: "🟦",
      purple: "🟪",
    };

    // Create a complete history including both guesses and found groups
    let completeHistory: { type: "guess" | "found"; data: string[] | Group }[] = [];

    // Add all attempts from guess history
    guessHistory.forEach((guess) => {
      const isCorrectGuess = gameData.groups.some((group) => {
        const groupWords = group.words.map((w) => w.toUpperCase());
        const guessWords = guess.map((w) => w.toUpperCase());
        return (
          guessWords.every((word) => groupWords.includes(word)) && groupWords.every((word) => guessWords.includes(word))
        );
      });

      if (!isCorrectGuess) {
        // Only add incorrect guesses (correct ones will be shown as solid color lines)
        let line = "";
        guess.forEach((word) => {
          const group = gameData.groups.find((g) => g.words.map((w) => w.toUpperCase()).includes(word.toUpperCase()));
          if (group) {
            line += colorEmojis[group.color];
          }
        });
        if (line.length > 0) {
          shareText += line + "\n";
        }
      } else {
        // For correct guesses, show a solid line of that color
        const matchedGroup = gameData.groups.find((group) => {
          const groupWords = group.words.map((w) => w.toUpperCase());
          const guessWords = guess.map((w) => w.toUpperCase());
          return guessWords.every((word) => groupWords.includes(word));
        });
        if (matchedGroup) {
          shareText += colorEmojis[matchedGroup.color].repeat(4) + "\n";
        }
      }
    });

    shareText += "\nPlay at nowadays.ai/games/connections";

    return shareText.trim();
  }, [guessHistory, gameData]);

  const handleShare = async () => {
    const shareText = generateShareText();

    try {
      await navigator.clipboard.writeText(shareText);
      setShareMessage("Results copied to clipboard!");
      setTimeout(() => setShareMessage(""), 3000);
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      setShareMessage("Failed to copy to clipboard");
      setTimeout(() => setShareMessage(""), 3000);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 pt-20">
        {isClubhouse && <img src={"/landing/clubhouse.png"} alt="Clubhouse" className="h-20 w-auto mb-8" />}
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4 text-black">Connections</h1>
          <p className="text-base text-gray-600">Loading today's puzzle...</p>
        </div>
      </div>
    );
  }

  if (!gameStarted) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 pt-20">
        {isClubhouse && <img src={"/landing/clubhouse.png"} alt="Clubhouse" className="h-20 w-auto mb-8" />}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-black">Connections</h1>
          <p className="text-lg text-gray-600 mb-2">Find groups of four words that share something in common</p>
          <p className="text-base text-gray-500">{getDayOfWeek()}</p>
        </div>

        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
          <div className="space-y-6">
            <input
              type="text"
              placeholder="Enter your name"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              className="w-full px-6 py-4 text-base border-2 border-purple-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              maxLength={20}
              onKeyDown={(e) => e.key === "Enter" && startGame()}
              autoFocus
            />

            {errorMessage && <div className="text-red-600 text-center font-medium">{errorMessage}</div>}

            <button
              onClick={startGame}
              className="w-full bg-purple-100 hover:bg-purple-200 text-purple-700 py-3 text-base rounded-xl font-bold transition-all transform hover:scale-105 shadow-sm"
            >
              Start Playing →
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 pt-20">
      {isClubhouse && <img src={"/landing/clubhouse.png"} alt="Clubhouse" className="h-20 w-auto mb-6" />}
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-4 sm:p-8 max-w-4xl w-full mx-4">
        <div className="text-center mb-4 sm:mb-6">
          <h1 className="text-xl sm:text-2xl font-bold mb-2 sm:mb-3 text-black">Connections</h1>
          <p className="text-sm sm:text-base text-gray-600">Hello {playerName}! Find four groups of four</p>

          {/* Live Stats */}
          <div className="flex justify-center gap-4 sm:gap-8 mt-3 sm:mt-4 text-sm sm:text-base font-semibold">
            <span className="text-gray-700">Found: {foundGroups.length}/4</span>
            <span className="text-gray-600">
              Mistakes: {mistakes}/{MAX_MISTAKES}
            </span>
            <span className="text-gray-600">Time: {formatTime(currentTime)}</span>
          </div>
        </div>

        {errorMessage && <div className="text-red-600 font-semibold text-center mb-3 text-sm">{errorMessage}</div>}

        {gameState === "won" && (
          <div className="text-green-600 font-semibold text-center mb-3">
            <p>Congratulations! You found all groups!</p>
            <p className="text-sm">Time: {formatTime(currentTime)}</p>
          </div>
        )}

        {gameState === "lost" && (
          <div className="text-red-600 font-semibold text-center mb-3">
            <p>Game over! Better luck next time!</p>
          </div>
        )}

        {/* Found Groups */}
        {foundGroups.length > 0 && (
          <div className="mb-6 space-y-2">
            {foundGroups.map((group, index) => (
              <div key={index} className={`p-4 rounded-lg ${GROUP_COLORS[group.color]}`}>
                <div className="font-bold text-lg mb-1">{group.theme}</div>
                <div className="text-sm">{group.words.join(", ")}</div>
              </div>
            ))}
          </div>
        )}

        {/* Word Grid */}
        {gameState === "playing" && shuffledWords.length > 0 && (
          <>
            <div
              className={`grid grid-cols-4 gap-1 sm:gap-2 mb-6 max-w-[600px] mx-auto ${shake ? "animate-pulse" : ""}`}
            >
              {shuffledWords.map((word, index) => (
                <button
                  key={`${word}-${index}`}
                  onClick={() => toggleWordSelection(word)}
                  className={`aspect-square rounded-lg font-semibold transition-all transform hover:scale-105 flex items-center justify-center px-1 sm:px-2 ${
                    selectedWords.includes(word)
                      ? "bg-purple-200 text-purple-800 border-2 border-purple-400"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-200"
                  }`}
                  style={{
                    fontSize:
                      word.length > 10
                        ? "0.65rem"
                        : word.length > 8
                          ? "0.7rem"
                          : word.length > 6
                            ? "0.75rem"
                            : "0.875rem",
                  }}
                >
                  <span className="text-center">{word.toUpperCase()}</span>
                </button>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2 sm:gap-3 justify-center">
              <button
                onClick={shuffleWords}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 sm:px-6 rounded-lg font-semibold text-sm sm:text-base transition-all"
              >
                Shuffle
              </button>

              <button
                onClick={deselectAll}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 sm:px-6 rounded-lg font-semibold text-sm sm:text-base transition-all"
                disabled={selectedWords.length === 0}
              >
                Deselect All
              </button>

              <button
                onClick={checkSelection}
                className={`py-2 px-4 sm:px-6 rounded-lg font-semibold text-sm sm:text-base transition-all ${
                  selectedWords.length === 4
                    ? "bg-purple-100 hover:bg-purple-200 text-purple-700"
                    : "bg-gray-100 text-gray-400 cursor-not-allowed"
                }`}
                disabled={selectedWords.length !== 4}
              >
                Submit
              </button>
            </div>
          </>
        )}

        {/* Show remaining groups when game is lost */}
        {gameState === "lost" && gameData && (
          <div className="mt-6 space-y-2">
            <p className="text-center font-semibold mb-3">The answers were:</p>
            {gameData.groups
              .filter((grp) => !foundGroups.some((fg) => fg.theme === grp.theme))
              .map((group, index) => (
                <div key={index} className={`p-4 rounded-lg ${GROUP_COLORS[group.color]} opacity-75`}>
                  <div className="font-bold text-lg mb-1">{group.theme}</div>
                  <div className="text-sm">{group.words.join(", ")}</div>
                </div>
              ))}
          </div>
        )}

        {/* Share, Certificate and Other Games Section */}
        {(gameState === "won" || gameState === "lost") && (
          <>
            <div className="mt-6 mb-8 text-center">
              <div className="flex gap-3 justify-center">
                <button
                  onClick={handleShare}
                  className="bg-purple-100 hover:bg-purple-200 text-purple-700 py-3 px-6 rounded-xl font-semibold text-base transition-all transform hover:scale-105 shadow-sm"
                >
                  Share Results
                </button>
                {gameState === "won" && (
                  <button
                    onClick={handleDownloadCertificate}
                    className="bg-purple-100 hover:bg-purple-200 text-purple-700 py-3 px-6 rounded-xl font-semibold text-base transition-all transform hover:scale-105 shadow-sm"
                  >
                    Download Certificate
                  </button>
                )}
              </div>
              {shareMessage && <div className="mt-3 text-green-600 font-medium text-sm">{shareMessage}</div>}
            </div>

            {/* Other Games Section */}
            <OtherGames currentGameId="connections" />
          </>
        )}
      </div>
    </div>
  );
}

export default function ConnectionsPage() {
  return (
    <Suspense
      fallback={
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-white/90 backdrop-blur-sm rounded-lg p-8 max-w-md w-full mx-4 text-center">
            <h1 className="text-2xl font-bold mb-4">Connections</h1>
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      }
    >
      <ConnectionsPageContent />
    </Suspense>
  );
}
